// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { Routes, Route, Navigate, Outlet, useParams } from 'react-router-dom';
import ProtectedRoute from '../common/ProtectedRoute';
import { usePersona } from '../../hooks/usePersona';
import { siteLandingPath } from './navModel';
import FlagRoute from '../common/FlagRoute';
import { tabForBucket } from '../../services/alarmSelectors';

import Login from '../../pages/Login';
import NotFound from '../../pages/NotFound';
import Profile from '../../pages/Profile';
import AgenticSearch from '../../pages/AgenticSearch';
import AgentPage from '../../pages/AgentPage';
import UsersPage from '../../pages/UsersPage';
import OnboardSite from '../../pages/OnboardSite';
import OnboardingDemo from '../../pages/OnboardingDemo';
import OnboardingWizard from '../../pages/onboarding/OnboardingWizard';

// New Carbon shell screens
import HomeAlarms from '../../pages/shell-screens/HomeAlarms';
import AlarmDetail from '../../pages/shell-screens/AlarmDetail';
import AnomalyDetail from '../../pages/shell-screens/AnomalyDetail';
import SopArtifactDetail from '../../pages/sop/SopArtifactDetail';
import Recommendations from '../../pages/shell-screens/Recommendations';
import RecommendationsScreen from '../../pages/shell-screens/RecommendationsScreen';
import FloorPlanScreen from '../../pages/shell-screens/FloorPlanScreen';
import LogScreen from '../../pages/shell-screens/LogScreen';
import AuditScreen from '../../pages/shell-screens/AuditScreen';
import Preferences from '../../pages/shell-screens/Preferences';
import WorkOrders from '../../pages/shell-screens/WorkOrders';
import SitePowerFlow from '../../pages/shell-screens/SitePowerFlow';
import SiteAssetDirectory from '../../pages/shell-screens/SiteAssetDirectory';
import SiteProcedures from '../../pages/shell-screens/SiteProcedures';
import PlanDetail from '../../pages/site-sections/schedule/PlanDetail';
import FleetOverview from '../../pages/shell-screens/FleetOverview';
import FleetOverviewDemo from '../../pages/shell-screens/demo/FleetOverviewDemo';
import SopGovernance from '../../pages/shell-screens/SopGovernance';
import RecordsTabs from './RecordsTabs';
import Scaffold from '../../pages/shell-screens/Scaffold';
import { useActiveSite } from '../../hooks/useActiveSite';
import { DISPLAY_LOCAL_ONLY } from '../../config/featureFlags';

/*
 * The power-flow demos draw the real components against fixtures, one of
 * which is a capture of a real site's twin. They are developer views reached
 * by URL, and a route guard protects a page, not the JavaScript that ships
 * it — so outside development they are not in the bundle at all.
 */
type LazyScreen = React.LazyExoticComponent<React.ComponentType> | null;
let SitePowerFlowComponentsDemo: LazyScreen = null;
let SitePowerFlowDiagramDemo: LazyScreen = null;
// A literal `if` on `NODE_ENV`, not a named constant or a ternary: that is the
// one shape the bundler treats as dead code and drops the imports behind.
if (process.env.NODE_ENV !== 'production') {
  SitePowerFlowComponentsDemo = React.lazy(
    () => import('../../pages/shell-screens/SitePowerFlowComponentsDemo')
  );
  SitePowerFlowDiagramDemo = React.lazy(
    () => import('../../pages/shell-screens/SitePowerFlowDiagramDemo')
  );
}

/**
 * Where a bare /recommendations (and every legacy plan bookmark) lands. Live is
 * the section's front door: it answers "what is running right now", and outside
 * a plan's window shows live data against today's baseline instead.
 */
const RECOMMENDATIONS_DEFAULT = '/recommendations/live';

/**
 * Gate for surfaces hidden behind DISPLAY_LOCAL_ONLY (SOP governance,
 * Onboarding, Bills, User management — see config/featureFlags.ts and
 * navModel.ts's LOCAL_ONLY_SECTIONS, which must stay in sync with this).
 *
 * navModel drops the rail items so they can't be clicked; this stops a
 * bookmark or typed URL from reaching them anyway. Redirects to the
 * persona's landing screen rather than rendering a 404, so an existing
 * bookmark lands somewhere useful instead of looking broken.
 */
function LocalOnlyRoute({ children }: { children: React.ReactNode }) {
  if (!DISPLAY_LOCAL_ONLY) return <PersonaHome />;
  return <>{children}</>;
}

/** Where a hidden site section sends the user: the site's Power Flow, not a dead end. */
function SiteSectionFallback() {
  const { site_id = '' } = useParams();
  return <Navigate to={`/site/${site_id}/power-flow`} replace />;
}

/**
 * The alarms home for one `/home/:bucket`. Every bucket that selects the
 * Routine tab (`routine`, the legacy `daily`) sits behind routine-tab-enabled
 * (AB#33171); `tabForBucket` is what decides that, so a new alias is covered.
 */
function HomeBucketRoute() {
  const { bucket } = useParams();
  if (tabForBucket(bucket) !== 'routine') return <HomeAlarms />;
  return (
    <FlagRoute flag="routineTabEnabled" fallback={<Navigate to="/home/alarms" replace />}>
      <HomeAlarms />
    </FlagRoute>
  );
}

/** Redirect "/" to the persona's landing screen. */
function PersonaHome() {
  const { persona } = usePersona();
  const to =
    persona === 'executive' ? '/executive' : persona === 'manager' ? '/fleet' : '/home/alarms';
  return <Navigate to={to} replace />;
}

/**
 * Guards /fleet at the route level. Nav only hides the Fleet rail item for
 * non-manager personas (navModel.ts); it never stopped a direct URL hit, so
 * Operators could reach Fleet and Multi-Site anyway (AB#30058).
 */
function RequireFleetAccess({ children }: { children: React.ReactNode }) {
  const { persona } = usePersona();
  if (persona !== 'manager') return <PersonaHome />;
  return <>{children}</>;
}

/** Redirect "/site" to the user's resolved site (from OMS / /me). */
function SiteRedirect() {
  const { siteId } = useActiveSite();
  return <Navigate to={siteLandingPath(siteId)} replace />;
}

/**
 * Power Flow replaced Asset Topology in the Site sub-nav. The old segment is
 * kept alive as a redirect for one release so existing bookmarks and any link
 * still pointing at it land somewhere useful rather than on a 404.
 */
function AssetTopologyRedirect() {
  const { site_id: siteId } = useParams();
  return <Navigate to={siteLandingPath(siteId ?? '')} replace />;
}

/**
 * Redirect legacy /site/:id/schedule[/:planId] bookmarks to the Recommendations
 * section, where the plan surface now lives. The plan detail route is keyed
 * by HITL approval ID now, which the legacy date-based :planId can't be
 * mapped to, so any bookmark (bare listing or a specific old plan) falls
 * back to the recommendations list.
 */
function LegacyScheduleRedirect() {
  return <Navigate to={RECOMMENDATIONS_DEFAULT} replace />;
}

// The onboarding wizard (coverage-checklist flow) is the /onboarding default
// for every site; the GA-10 streaming demo stays at /onboarding/ga10 until
// the wizard reaches full parity, and the OMS new-site record wizard moves
// to /onboarding/new-site (the onboarding wizard links to it in fleet mode).

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <PersonaHome />
          </ProtectedRoute>
        }
      />

      {/* Home / alarms ---------------------------------------------------- */}
      <Route path="/home" element={<Navigate to="/home/alarms" replace />} />
      <Route
        path="/home/recommendations"
        element={
          <ProtectedRoute>
            <Recommendations />
          </ProtectedRoute>
        }
      />
      {/* Legacy operator-only path, superseded by the role-aware view above. */}
      <Route
        path="/home/approved-recommendations"
        element={<Navigate to="/home/recommendations" replace />}
      />
      <Route
        path="/home/:bucket"
        element={
          <ProtectedRoute>
            <HomeBucketRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path="/alarms/:alarmCode"
        element={
          <ProtectedRoute>
            <AlarmDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/anomalies/:alarmCode"
        element={
          <ProtectedRoute>
            <AnomalyDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sops/:id"
        element={
          <ProtectedRoute>
            <SopArtifactDetail />
          </ProtectedRoute>
        }
      />

      {/* Site ------------------------------------------------------------- */}
      <Route
        path="/site/:site_id/power-flow"
        element={
          <ProtectedRoute>
            <SitePowerFlow />
          </ProtectedRoute>
        }
      />
      {/* The symbol layer, rendered from the real components against fixtures.
          Deliberately not in SiteTabs: a developer view, reached by URL. It
          needs no twin data, which is what lets it show fault, warn and the
          deferred symbols at all. Development builds only — see above. */}
      {SitePowerFlowComponentsDemo && (
        <Route
          path="/site/:site_id/power-flow/components-demo"
          element={
            <ProtectedRoute>
              <React.Suspense fallback={null}>
                <SitePowerFlowComponentsDemo />
              </React.Suspense>
            </ProtectedRoute>
          }
        />
      )}
      {/* The same drawing, against a site that publishes what the one-line
          needs. The live page draws less because the twin publishes less; this
          is what closing the readiness gaps would buy. */}
      {SitePowerFlowDiagramDemo && (
        <Route
          path="/site/:site_id/power-flow/diagram-demo"
          element={
            <ProtectedRoute>
              <React.Suspense fallback={null}>
                <SitePowerFlowDiagramDemo />
              </React.Suspense>
            </ProtectedRoute>
          }
        />
      )}
      <Route path="/site/:site_id/asset-topology" element={<AssetTopologyRedirect />} />
      <Route
        path="/site/:site_id/asset-directory"
        element={
          <ProtectedRoute>
            <SiteAssetDirectory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/site/:site_id/floor-plan"
        element={
          <ProtectedRoute>
            <FloorPlanScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/site/:site_id/procedures"
        element={
          <ProtectedRoute>
            <FlagRoute flag="siteProceduresEnabled" fallback={<SiteSectionFallback />}>
              <SiteProcedures />
            </FlagRoute>
          </ProtectedRoute>
        }
      />
      {/* SOP binding coverage is scoped to the selected site. The site is shell
          state, not a route param — a single-page app carries the selection
          across screens, so the route stays site-agnostic and the screen reads
          the active site. */}
      <Route
        path="/sop-governance"
        element={
          <ProtectedRoute>
            <LocalOnlyRoute>
              <SopGovernance />
            </LocalOnlyRoute>
          </ProtectedRoute>
        }
      />
      {/* Legacy schedule bookmarks → Optimizations (must precede the catch-all). */}
      <Route
        path="/site/:site_id/schedule/*"
        element={
          <ProtectedRoute>
            <LegacyScheduleRedirect />
          </ProtectedRoute>
        }
      />
      <Route path="/site" element={<SiteRedirect />} />

      {/* Notes (Log) / Audit — one section, split by the Records tab bar ---- */}
      <Route
        element={
          <ProtectedRoute>
            <div className="hs-screen">
              <RecordsTabs />
              <div className="hs-screen-body">
                <Outlet />
              </div>
            </div>
          </ProtectedRoute>
        }
      >
        <Route
          path="/log"
          element={
            <FlagRoute flag="notesAuditEnabled" fallback={<PersonaHome />}>
              <LogScreen />
            </FlagRoute>
          }
        />
        <Route
          path="/audit"
          element={
            <FlagRoute flag="notesAuditEnabled" fallback={<PersonaHome />}>
              <AuditScreen />
            </FlagRoute>
          }
        />
      </Route>

      {/* Work orders (spec 005) — external provider oversight -------------- */}
      <Route
        path="/work-orders"
        element={
          <ProtectedRoute>
            <FlagRoute flag="workOrdersEnabled" fallback={<PersonaHome />} waitForFlags={false}>
              <WorkOrders />
            </FlagRoute>
          </ProtectedRoute>
        }
      />

      {/* Preferences ------------------------------------------------------ */}
      <Route
        path="/prefs"
        element={
          <ProtectedRoute>
            <Preferences />
          </ProtectedRoute>
        }
      />

      {/* Fleet ------------------------------------------------------------ */}
      <Route
        path="/fleet"
        element={
          <ProtectedRoute>
            <RequireFleetAccess>
              <div className="hs-screen">
                <div className="hs-screen-body">
                  <Outlet />
                </div>
              </div>
            </RequireFleetAccess>
          </ProtectedRoute>
        }
      >
        <Route index element={<FleetOverview />} />
        {/* Scripted fleets for exercising the map's auto-fit framing, which
            the real one-site fleet cannot show. Deliberately not in
            FleetTabs — it is reachable by URL only, and every scenario
            banners itself as scripted (see demo/FleetOverviewDemo.tsx). */}
        {/* /fleet/demo lists the scenarios rather than redirecting to the
            first one: landing on a scripted fleet you did not ask for reads
            as the real fleet overview having gone wrong. */}
        <Route path="demo" element={<FleetOverviewDemo />} />
        <Route path="demo/:scenario" element={<FleetOverviewDemo />} />
        <Route
          path="audit"
          element={
            <Scaffold
              title="Energy Fluo Audit"
              blurb="System-of-record event timeline and regional posture."
            />
          }
        />
      </Route>
      <Route path="/recommendations" element={<Navigate to={RECOMMENDATIONS_DEFAULT} replace />} />
      {/* The tab slugs were renamed when the Live tab became real. Redirect the
          old ones explicitly: RecommendationsScreen's "unknown tab -> default"
          fallback would swallow these bookmarks without saying so. */}
      <Route
        path="/recommendations/new"
        element={<Navigate to="/recommendations/tomorrow" replace />}
      />
      <Route
        path="/recommendations/in-progress"
        element={<Navigate to="/recommendations/live" replace />}
      />
      <Route
        path="/recommendations/history"
        element={<Navigate to="/recommendations/past" replace />}
      />
      <Route
        path="/recommendations/:tab"
        element={
          <ProtectedRoute>
            <RecommendationsScreen />
          </ProtectedRoute>
        }
      />
      {/* Legacy /optimizations bookmarks → the renamed Recommendations section. */}
      <Route path="/optimizations" element={<Navigate to={RECOMMENDATIONS_DEFAULT} replace />} />
      {/* Optimization plan detail — scoped to the Recommendations section (not
          Site). ProtectedRoute rather than role-gated, so operators reaching the
          list from Home can open a plan too. Keyed by HITL approval ID. */}
      <Route
        path="/optimizations/:approvalId/*"
        element={
          <ProtectedRoute>
            <PlanDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <LocalOnlyRoute>
              <OnboardingWizard />
            </LocalOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/new-site"
        element={
          <ProtectedRoute>
            <LocalOnlyRoute>
              <OnboardSite />
            </LocalOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/ga10"
        element={
          <ProtectedRoute>
            <LocalOnlyRoute>
              <OnboardingDemo />
            </LocalOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bills"
        element={
          <ProtectedRoute>
            <LocalOnlyRoute>
              <Scaffold
                title="Bills"
                blurb="Management-only billing surface. Not part of this PR's scope."
              />
            </LocalOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <LocalOnlyRoute>
              <UsersPage />
            </LocalOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/access"
        element={
          <ProtectedRoute>
            <Scaffold
              title="Access & permissions"
              blurb="Role-assignment management. Not part of this PR's scope."
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/executive"
        element={
          <ProtectedRoute>
            <Scaffold
              title="Fleet posture"
              blurb="Executive portfolio posture — sites healthy, attention, open escalations, compliance."
            />
          </ProtectedRoute>
        }
      />

      {/* Backwards-compatible redirects for old bookmarks ---------------- */}
      <Route path="/admin" element={<Navigate to="/fleet" replace />} />
      <Route path="/admin/users" element={<Navigate to="/users" replace />} />
      <Route path="/admin/onboard" element={<Navigate to="/onboarding" replace />} />
      <Route path="/admin/onboarding-demo" element={<Navigate to="/onboarding/ga10" replace />} />
      <Route path="/admin/audit" element={<Navigate to="/audit" replace />} />
      <Route
        path="/admin/agent"
        element={
          <ProtectedRoute>
            <AgentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/search"
        element={
          <ProtectedRoute>
            <AgenticSearch />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users/:id"
        element={
          <ProtectedRoute>
            <LocalOnlyRoute>
              <Profile />
            </LocalOnlyRoute>
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
